import * as EL from "esri-leaflet";
import L from "leaflet";
import MapConfig from "../utils/map-config.json";
import { getDatasetsJson } from "../utils/DatasetsJson";

export const disableMapEvents = (panelRef) => {
  if (!panelRef?.current) return;

  const container = panelRef.current;

  const stopPropagation = (e) => {
    e.stopPropagation();
    if (e.type === "dblclick") {
      e.preventDefault();
    }
  };

  const events = ["wheel", "touchstart", "touchmove", "mousedown", "mousemove", "mouseup", "dblclick"];

  events.forEach((event) => {
    container.addEventListener(event, stopPropagation, { passive: false, capture: true });
  });

  return () => {
    events.forEach((event) => {
      container.removeEventListener(event, stopPropagation, { capture: true });
    });
  };
};

export const updateUrl = (layerIdOrList) => {
  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.search);

  let activeLayers;

  if (Array.isArray(layerIdOrList)) {
    activeLayers = layerIdOrList;
  } else {
    const layerParam = params.get("layer");
    activeLayers = layerParam ? layerParam.split(",").map(Number).filter(n => !isNaN(n)) : [];

    const index = activeLayers.indexOf(layerIdOrList);
    if (index === -1) {
      activeLayers.push(layerIdOrList);
    } else {
      activeLayers.splice(index, 1);
    }
  }

  if (activeLayers.length > 0) {
    params.set("layer", activeLayers.join(","));
  } else {
    params.delete("layer");
  }

  const newUrl = url.pathname + "?" + params.toString();
  window.history.pushState({}, "", newUrl);
};

export const setLoadListener = (layerObj, layers) => {
  const { layer } = layerObj;

  layer.off("load");

  layer.on("load", () => {
    const currentLayer = layers.find(l => l.layer === layer);
    const currentOpacity = currentLayer?.opacity ?? 1;

    if (layer._isUnesco) {
      applyOpacityToPoints(layer, currentOpacity);
    } else
    if (layer._symbol) {
      applySymbolStyleWithOpacity(layer, layer._symbol, currentOpacity);
    } else if (typeof layer.setOpacity === "function") {
      layer.setOpacity(currentOpacity);
    } else {
      applyOpacityToPoints(layer, currentOpacity);
    }
  });
};

export const getDataset = (layerNum) => {
  const datasets = getDatasetsJson();
  if (!datasets) return null;
  for (const provider of datasets) {
    for (const dataset of provider.datasets || []) {
      const found = dataset.layers?.some(layer => layer.num_layer === layerNum);
      if (found) return dataset;
    }
  }
  return null;
};

export const applySymbolStyleWithOpacity = (layerInstance, symbol, opacity) => {
  const fillOpacity = opacity;
  const strokeOpacity = opacity;

  const fillColor = `rgba(${symbol.color[0]},${symbol.color[1]},${symbol.color[2]},${symbol.color[3] / 255 * fillOpacity})`;

  const outlineColor = symbol.outline
    ? `rgba(${symbol.outline.color[0]},${symbol.outline.color[1]},${symbol.outline.color[2]},${symbol.outline.color[3] / 255 * strokeOpacity})`
    : "#000";

  const styleObj = {
    radius: symbol.size,
    fillColor,
    color: outlineColor,
    weight: symbol.outline?.width || 0,
    fillOpacity,
    opacity: strokeOpacity,
  };

  if (layerInstance.getLayers) {
    layerInstance.getLayers().forEach(feature => {
      if (feature.setStyle) {
        feature.setStyle(styleObj);
      }
    });
  } else if (layerInstance._layers) {
    Object.values(layerInstance._layers).forEach(feature => {
      if (feature.setStyle) {
        feature.setStyle(styleObj);
      }
    });
  }
};

export const applyOpacityToPoints = (layerInstance, opacity) => {
  if (layerInstance._isUnesco && layerInstance._layers) {
    Object.values(layerInstance._layers).forEach((featureLayer) => {
      if (featureLayer.setStyle && featureLayer.options) {
        featureLayer.setStyle({
          ...featureLayer.options,
          fillOpacity: opacity,
          opacity: opacity,
        });
      }
    });
    return;
  }

  if (layerInstance.getLayers) {
    layerInstance.getLayers().forEach((feature) => {
      if (feature.setStyle) {
        feature.setStyle({
          fillOpacity: opacity,
          opacity: opacity,
        });
      }
    });
  } else if (layerInstance._layers) {
    Object.values(layerInstance._layers).forEach((feature) => {
      if (feature.setStyle) {
        feature.setStyle({
          fillOpacity: opacity,
          opacity: opacity,
        });
      }
    });
  }
};

export const addLayerToMap = async (map, layers, url, id, layer, layerData) => {
  if (!url || layers.some(a => a.id === id)) return Promise.resolve(null);

  const isWmts = layerData.type === "WMTS" || url.toLowerCase().includes("/wmts");
  const isWms = layerData.type === "WMS" || url.toLowerCase().includes("/wms");
  const isWfs = layerData.type === "WFS" || url.toLowerCase().includes("/wfs");

  const existingOpacity = layers.find(l => l.id === id)?.opacity ?? 1;

  const getTileMatrixFormat = async (url, tileMatrixSet) => {
    const capabilitiesUrl = `${url.split("?")[0]}?SERVICE=WMTS&REQUEST=GetCapabilities&VERSION=1.0.0`;

    try {
      const response = await fetch(capabilitiesUrl);
      const text = await response.text();
      const parser = new DOMParser();
      const xml = parser.parseFromString(text, "application/xml");
 
      const tileMatrixSetElement = [...xml.querySelectorAll("TileMatrixSet")]
        .find(set => set.querySelector("Identifier")?.textContent === tileMatrixSet);

      if (!tileMatrixSetElement) return true;

      const firstTileMatrixId = tileMatrixSetElement.querySelector("TileMatrix > Identifier")?.textContent;

      return firstTileMatrixId?.startsWith(tileMatrixSet + ":") ? `\${tileMatrixSet}:\${z}` : `\${z}`;
    } catch (err) {
      console.warn("Error getting GetCapabilities:", err);
      return true;
    }
  };

  if (isWmts) {
    const layerName = typeof layer === "string" ? layer : layer.layer;
    const tileMatrixSet = layerData.TileMatrixSetLink?.[0]?.TileMatrixSet || "EPSG:3857";
    let tileMatrix = layerData.TileMatrixSetLink?.[0]?.TileMatrix || await getTileMatrixFormat(url, tileMatrixSet);
    let baseUrl = url.includes(`\${z}`) ? url : `${url.endsWith('?') ? url.slice(0, -1) : url}?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=${layerName}&FORMAT=image/png&TILEMATRIXSET=${tileMatrixSet}&TILEMATRIX=${tileMatrix}&TILEROW=\${y}&TILECOL=\${x}`;

    const wmtsLayer = L.tileLayer("", {
      tileSize: 256,
      maxZoom: 18,
      opacity: existingOpacity,
      noWrap: true,
    });

    wmtsLayer.getTileUrl = (coords) => {
      const z = coords.z;
      const x = coords.x;
      const y = coords.y;

      return baseUrl.replace(`\${z}`, z).replace(`\${x}`, x).replace(`\${y}`, y);
    };

    wmtsLayer._layerType = "WMTS";
    wmtsLayer.addTo(map);
    wmtsLayer.bringToFront();
    setLoadListener({ layer: wmtsLayer, opacity: existingOpacity }, layers);
    return wmtsLayer;

  } else if (isWms) {
    const wmsLayer = L.tileLayer.wms(url, {
      layers: layer,
      format: "image/png",
      transparent: true,
      opacity: existingOpacity,
      attribution: false,
      noWrap: true,
    });

    wmsLayer._layerType = "WMS";
    wmsLayer.addTo(map);
    wmsLayer.bringToFront();
    setLoadListener({ layer: wmsLayer, opacity: existingOpacity }, layers);

    if (wmsLayer.options.attribution && map.attributionControl) {
      map.attributionControl.removeAttribution(wmsLayer.options.attribution);
    }
    return wmsLayer;

  } else if (isWfs) {
    const layerName = typeof layer === "string" ? layer : layer.layer;
    const existingOpacity = layers.find(l => l.id === id)?.opacity ?? 1;
    const format = getDataset(id).format;
    const totalFeatures = layerData.total_count || 10000;
    const batchSize = 1000;

    const wfsLayer = L.geoJSON([], {
      style: feature => {
        const type = feature.geometry?.type;
        if (type === "LineString" || type === "MultiLineString") {
          return { color: "#615C9F", weight: 3, opacity: existingOpacity };
        }
        if (type === "Polygon" || type === "MultiPolygon") {
          return { color: "#615C9F", fillColor: "#B8B3D9", weight: 2, opacity: existingOpacity, fillOpacity: existingOpacity };
        }
        return null;
      },
      pointToLayer: (feature, latlng) => L.circleMarker(latlng, {
        radius: 4,
        fillColor: "#615C9F",
        color: "#000",
        weight: 1,
        opacity: existingOpacity,
        fillOpacity: existingOpacity
      }),
      onEachFeature: (feature, layer) => {
        const props = feature.properties;
        let popupContent = `<div class="custom-popup" style="margin-top: 1rem; overflow: auto; max-height: 15rem;"><div class="popup-body"><table class="popup-table">`;
        for (let key in props) {
          popupContent += `<tr><td>${key}</td><td>${props[key]}</td></tr>`;
        }
        popupContent += `</table></div></div>`;
        layer.bindPopup(popupContent);
      }
    }).addTo(map);

    wfsLayer._layerType = "WFS";

    setLoadListener({ layer: wfsLayer, opacity: existingOpacity }, layers);

    let startIndex = 0;
    while (startIndex < totalFeatures) {
      const batchGeoJSON = await fetchWFSPaged(url, layerName, format, totalFeatures, batchSize, startIndex);
      if (batchGeoJSON.features.length === 0) break;

      wfsLayer.addData(batchGeoJSON);

      startIndex += batchSize;
      if (batchGeoJSON.features.length < batchSize) break;
    }

    return wfsLayer;

  } else {
    return fetch(url + "?f=json")
      .then((response) => response.json())
      .then((data) => {
        if (data.hasOwnProperty("copyrightText")) {
          data.copyrightText = "";
        }

        let layer;
        const hasDrawingInfo = !!(data.drawingInfo && data.drawingInfo.renderer && data.drawingInfo.renderer.symbol);

        if (data.singleFusedMapCache) {
          layer = new EL.TiledMapLayer({ url, opacity: existingOpacity, attribution: false, noWrap: true, });
        } else if (data.layers || data.type.toLowerCase().includes("feature")) {
          if (hasDrawingInfo && data.drawingInfo.renderer.symbol.type === "esriSMS") {
            const isUnesco = layerData.name_en.includes("UNESCO");
            const symbol = data.drawingInfo.renderer.symbol;
            const rgba = `rgba(${symbol.color[0]},${symbol.color[1]},${symbol.color[2]},${symbol.color[3] / 255})`;
            const outlineColor = symbol.outline
              ? `rgba(${symbol.outline.color[0]},${symbol.outline.color[1]},${symbol.outline.color[2]},${symbol.outline.color[3] / 255})`
              : "#000";

            layer = new EL.FeatureLayer({
              url,
              attribution: false,
              pointToLayer: (feature, latlng) => {
                  return L.circleMarker(latlng, {
                    radius: symbol.size,
                    fillColor: isUnesco ? getUnescoStyles(feature.properties) : rgba,
                    color: outlineColor,
                    weight: symbol.outline?.width || 0,
                    fillOpacity: existingOpacity,
                    opacity: existingOpacity,
                  });
              },
                onEachFeature: (feature, layer) => {
                  if (isUnesco) {
                    loadCustomPopup(feature, layer);
                  }
                  else {
                    layer.on("click", () => {
                    const props = layer.feature.properties;
                      let popupContent = `<div class="custom-popup" style="margin-top: 1rem; overflow: auto; max-height: 15rem;"><div class="popup-body"><table class="popup-table">`;

                      for (let key in props) {
                        popupContent += `<tr><td>${key}</td><td>${props[key]}</td></tr>`;
                      }

                      popupContent += `</table></div></div>`;
                      layer.bindPopup(popupContent).openPopup();
                    });
                  }
                }
              });

            layer._symbol = symbol;
            if (isUnesco) {
              layer._isUnesco = true;
            }

          } else if (hasDrawingInfo) {
            layer = new EL.FeatureLayer({ url, attribution: false });

          } else {
            layer = new EL.FeatureLayer({
              url,
              attribution: false,
              pointToLayer: (feature, latlng) => L.circleMarker(latlng, {
                radius: 4,
                fillColor: "#615C9F",
                color: "#000",
                weight: 1,
                fillOpacity: existingOpacity,
                opacity: existingOpacity
              })
            });
          }

          layer.on("load", () => {
            if (layer.getLayers) {
              layer.getLayers().forEach(f => {
                if (f.setStyle) {
                  f.setStyle({
                    fillOpacity: existingOpacity,
                    opacity: existingOpacity
                  });
                }
              });
            } else if (layer._layers) {
              Object.values(layer._layers).forEach(f => {
                if (f.setStyle) {
                  f.setStyle({
                    fillOpacity: existingOpacity,
                    opacity: existingOpacity,
                    noWrap: true,
                  });
                }
              });
            }
          });

        } else {
          layer = new EL.TiledMapLayer({ url, opacity: existingOpacity, attribution: false, noWrap: true, });
        }

        layer.addTo(map);
        layer.bringToFront();
        setLoadListener({ layer, opacity: existingOpacity }, layers);

        if (layer.options.attribution && map.attributionControl) {
          map.attributionControl.removeAttribution(layer.options.attribution);
        }

        return layer;
      })
      .catch((error) => {
        console.error("Error loading layer:", error);
        return null;
      });
  }
};

export const fetchWFSPaged = async (baseUrl, typeName, format, totalFeatures, batchSize, startIndex = 0) => {
  const url =
    `${baseUrl}?service=WFS&version=2.0.0&request=GetFeature` +
    `&typeName=${encodeURIComponent(typeName)}` +
    `&outputFormat=${format}&srsName=EPSG:4326` +
    `&count=${batchSize}&startIndex=${startIndex}`;

  const response = await fetch(url);
  if (!response.ok) {
    console.error("WFS batch fetch failed", response.status);
    return { type: "FeatureCollection", features: [] };
  }

  const json = await response.json();
  return { type: "FeatureCollection", features: json.features || [] };

};

export const getUnescoStyles = (properties) => {
  let index;

  switch (true) {
    case (properties.property_category === "Cultural" || properties.property_category === "Mixed") && properties.property_danger === 0:
      index = 0;
      break;

    case (properties.property_category === "Natural") && properties.property_danger === 0:
      index = 1;
      break;

    case properties.property_danger === 1:
      index = 2;
      break;

    default:
      index = -1;
  }

  return MapConfig.LayerStyles.find(a => a.name === "UNESCO").style[index].color;
}

export const loadCustomPopup = (feature, layer) => {
  const formatNumber = (num) => {
    const parsed = parseFloat(num);
    if (isNaN(parsed)) return "";

    const partes = parsed.toFixed(2).split(".");
    partes[0] = partes[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    return partes.join(",");
  }

  layer.on("click", () => {
    let content =
      `<div class="custom-popup">
        <div class="popup-title">${feature.properties.property_name_en}</div>
        <div class="popup-scroll">
          <div class="popup-body">
            <div class=popup-description>${feature.properties.property_short_description_en}</div>
            <table class="popup-table">
              <tr><td>State(s) Party(ies)</td><td>${feature.properties.property_states_name_en}</td></tr>
              <tr><td>Category</td><td>${feature.properties.property_category}</td></tr>
              <tr><td>Date of inscription</td><td>${feature.properties.property_inscribed}</td></tr>
              <tr><td>Area of property (ha)</td><td>${formatNumber(feature.properties.property_core_area)}</td></tr>
              <tr><td>Area of buffer zone (ha)</td><td>${formatNumber(feature.properties.property_buffer_area)}</td></tr>
            </table>
            <a class="ccl-link popup-link" href="https://whc.unesco.org/en/list/${feature.properties.property_id}" target="_blank" rel="noopener noreferrer">
              Web page<div class="detail-icon"><i class="fa-solid fa-arrow-up-right-from-square"></i></div>
            </a>
            ${feature.properties.element_precision_publish === "1" ?
          `<img class="popup-image" src="https://whc.unesco.org/uploads/sites/site_${feature.properties.property_id}-500-500.jpg"/>` : ""}
          </div>
        </div>
      </div>`;
    layer.bindPopup(content).openPopup();
  });
}
